const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

/**
* @typedef {Object} CommentResult
* @property {number} IDComment
* @property {number} CreatingIDUser
* @property {string} Comment
* @property {string} Date
* @property {number} IsCreatingUser
* @property {Array<CommentResult>} [Children]
* @property {Object} [CreatingUserData]
* @property {string} [CreatingUserData.NameFirst]
* @property {string} [CreatingUserData.NameLast]
* @property {string} [_RowID]
*/

class CommentCurrent extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this.initialResultsLoaded = false;

		this.DefaultSearchFilters = {
			IDCustomer: null,
			PageSize: 25,
			CurrentPage: 1,
			TotalPages: 1,
			SortBy: 'Date',
			SortDirection: 'DESC',
		};
		this.CurrentSearchEntity = '';
		this.CurrentSearchFilters = JSON.parse(JSON.stringify(this.DefaultSearchFilters));
		this.CurrentSearchFiltersCount = 0;
		this.CurrentSearchResults = [];
	}

	onBeforeRenderAsync(pCallback)
	{
		this.CurrentSearchFilters.IDCustomer = this.AppData?.DocumentData?.ReportData?.IDCustomer || this.AppData?.CurrentUser?.CustomerID || 0;
		this.CurrentSearchFilters.IDDocument = this.AppData?.DocumentData?.ReportData?.IDDocument;
		if (pCallback)
		{
			pCallback();
		};
	}

	async onAfterRenderAsync(pCallback)
	{
		// if we don't have results, fetch them
		if (!this.initialResultsLoaded)
		{
			this.initialResultsLoaded = true;
			this.fetchComments(null, null, null);
		}
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * This method fetches all comments.
	 * @param {Event} event - The event that triggered the search.
	 * @param {string} pRowID - The ID of the row in the treeview.
	 * @param {boolean} pIsChild - Whether the row is a child row.
	 * @return {Promise<void>}
	 * @async
	 */
	async fetchComments(event, pRowID, pIsChild)
	{
		// prevent the browser from refreshing on form submissions
		if (event)
		{
			event.preventDefault();
		}
		// if new comment container exists
		if (document.getElementById('CommentCurrent-Add-Comment-Container'))
		{
			// hide add new comment container
			document.getElementById('CommentCurrent-Add-Comment-Container').classList.add('is-hidden');
		}
		// collapse the filter panel to get out the way of the results
		this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('CommentCurrentFilters', 'close');
		// simulate that data is loading in the results table
		this.pict.views.HeadlightPanels.addLoadingSkeletonRows('CommentCurrent-Results-Container', this.CurrentSearchFilters.PageSize, '6rem');
		// TODO: this should probably be moved to a provider but keeping it here for now because it works
		/**
		 * @callback GetCommentStructureCallback
		 * @param {CommentResult[]|any} pResult
		 * @return {void}
		 */
		/**
		 * Fetches the comment structure and updates the UI.
		 * @param {string|null} pRowID
		 * @param {boolean|null} pIsChild
		 * @param {Event|null} event
		 * @return {Promise<void>}
		 */
		this.pict.providers.CommentProvider.getCommentStructure(this.CurrentSearchFilters.IDCustomer, this.CurrentSearchFilters.IDDocument, this.AppData?.DocumentData?.ReportData?.Comments || [],
			/** @type {GetCommentStructureCallback} */
			(pResult) =>
			{
				if (!Array.isArray(pResult))
				{
					// Render comment results template (non-blocking, no side effects here)
					this.pict.views.CommentCurrent.renderAsync('CommentCurrent', '#CommentCurrent-Entry-Container', false, false, () =>
					{
						try
						{
							if (this.CommentCurrentResultsCallback)
							{
									// eslint-disable-next-line new-cap
									this.CommentCurrentResultsCallback(this.CurrentSearchResults);
							}
							else
							{
									this.updateWithResults(/** @type {CommentResult[]} */(pResult), pRowID, pIsChild);
							}
						}
						catch (innerError)
						{
								this.pict.log.error('Error in renderAsync callback:', innerError);
						}
					},
					);
				}
				else
				{
					this.updateWithResults(/** @type {CommentResult[]} */(pResult), pRowID, pIsChild);
				}
			},
		);
	}

	/**
	 * Set the callback function for the search results.
	 * @param {Function} pCallback - The callback function to set.
	 * @return {void}
	 * @example
	 * setCommentResultsCallback((pResults) => { console.log(pResults); });
	 */
	setCommentCurrentResultsCallback(pCallback)
	{
		// TODO: not sure if this is the best way to handle this, but it works for now.
		this.CommentCurrentResultsCallback = pCallback;
	}

	/**
	 * This method updates the IsCreatingUser property for each comment
	 * in the results to display edit and delete buttons correctly.
	 * @param {CommentResult[]} comments
	 * @return {void}
	 */
	updateIsCreatingUser(comments)
	{
		comments.forEach((comment) =>
		{
			comment.IsCreatingUser = (this.pict.AppData.CurrentUser.UserID === comment.CreatingIDUser) ? 1 : 0;

			if (Array.isArray(comment.Children) && comment.Children.length > 0)
			{
				this.updateIsCreatingUser(comment.Children); // recursive call
			}
		});
	}

	/**
	 * This method updates the results/UI based on new results and total count passed in.
	 * @param {Array} pResults - The results to update with.
	 * @param {string} [pRowID] - The ID of the row to update.
	 * @param {boolean} [pIsChild] - Whether the row is a child row.
	 * @return {void}
	 */
	updateWithResults(pResults, pRowID, pIsChild)
	{
		try
		{
			// update our results list
			this.CurrentSearchResults = pResults;
			// check if the current user is the same as the user who created the comment
			this.updateIsCreatingUser(this.CurrentSearchResults);
			// display results in descending order by updated date
			this.CurrentSearchResults.sort((a, b) =>
			{
				const dateA = new Date(a.Date);
				const dateB = new Date(b.Date);
				return dateB.getTime() - dateA.getTime(); // Sort in descending order
			});

			const pTotalCount = pResults?.length || 0;

			// update the total possible count so we can calculate the total pages
			this.CurrentSearchFilters.TotalPages = Math.ceil(pTotalCount / this.CurrentSearchFilters.PageSize);
			// render the search results view
			this.pict.views.CommentCurrent.renderAsync('CommentCurrent-Results', '#CommentCurrent-Results-Container', false, () =>
			{
				// render the pagination buttons
				this.pict.views.CommentCurrent.renderAsync('CommentCurrent-Pagination', '#CommentCurrent-Pagination-Container', false, () =>
				{
					this.log.info('Comment Search Pagination rendered');
				});
				// if there is a callback function, call it
				if (this.CommentCurrentResultsCallback)
				{
					// eslint-disable-next-line new-cap
					this.CommentCurrentResultsCallback(this.CurrentSearchResults);
				};
			});
		}
		catch (outerError)
		{
			this.pict.log.error('Failed to update with comment results:', outerError);
		}
	}

	/**
	 * This method adds a new comment.
	 * @param {Event} [event] - The event that triggered the search.
	 * @return {Promise<void>}
	 * @async
	 */
	addNewComment(event)
	{
		if (!this.pict.AppData.DocumentData.ReportData.IDDocument)
		{
			this.log.error(`No Document; can't add comment to document that doesn't exist: .`);
			this.pict.views.HeadlightPanels?.toast?.({
				message: `No Document; can't add comment to document that doesn't exist: .`,
				type: 'is-danger',
			});
			return;
		}
		// if user clicked the add comment button
		if (event)
		{
			// if editable container exists
			if (document.getElementById('CommentCurrent-Add-Comment-Container'))
			{
				// show add new comment container
				document.getElementById('CommentCurrent-Add-Comment-Container').classList.remove('is-hidden');
			}
		}
	}

	/**
	 * This method hides the new comment container on cancel event.
	 * @param {Event} event - The event that triggered the search.
	 * @param {string} [pIDComment] - The comment ID of the comment in the treeview.
	 * @async
	 */
	cancelCommentEdit(event, pIDComment)
	{
		// if user clicked the add comment button
		if (event)
		{
			// if new comment container exists
			if (document.getElementById('CommentCurrent-Add-Comment-Container'))
			{
				// hide reply comment container
				document.getElementById('CommentCurrent-Add-Comment-Container').classList.add('is-hidden');
			}
			// if reply container exists
			if (document.getElementById(`CommentCurrent-Reply-Comment-Container_${pIDComment}`))
			{
				// hide reply comment container
				document.getElementById(`CommentCurrent-Reply-Comment-Container_${pIDComment}`).classList.add('is-hidden');
			}
			// if editable container exists
			if (document.getElementById(`CommentCurrent-Edit-Comment_${pIDComment}`))
			{
				// swap editable textarea for readonly
				document.getElementById(`CommentCurrent-Edit-Comment_${pIDComment}`).classList.add('is-hidden');
				document.getElementById(`CommentCurrent-Readonly-Comment_${pIDComment}`).classList.remove('is-hidden');
			}
		}
	}

	/**
	 * This method adds a new comment.
	 * @param {Event} event - The event that triggered the search.
	 * @param {string} pIDComment - The ID of the comment to reply to.
	 * @return {Promise<void>}
	 * @async
	 */
	handleCommentReplyButton(event, pIDComment)
	{
		if (!pIDComment)
		{
			this.log.error(`No Comment ID; can't reply to comment that doesn't exist: .`);
			this.pict.views.HeadlightPanels?.toast?.({
				message: `No Comment ID; can't reply to comment that doesn't exist: .`,
				type: 'is-danger',
			});
			return;
		}
		// if user clicked the reply to comment button
		if (event)
		{
			// if child and editable containers exist
			if (document.getElementById(`CommentCurrent-Child-Comment_${pIDComment}`) && document.getElementById(`CommentCurrent-Reply-Comment-Container_${pIDComment}`))
			{
				// show reply comment container under the comment
				document.getElementById(`CommentCurrent-Child-Comment_${pIDComment}`).classList.remove('is-hidden');
				document.getElementById(`CommentCurrent-Reply-Comment-Container_${pIDComment}`).classList.remove('is-hidden');
			}
		}
	}

	/**
	 * This method edits a comment.
	 * @param {Event} event - The event that triggered the edit.
	 * @param {string} pIDComment - The ID of the comment to edit.
	 * @return {Promise<void>}
	 * @async
	 */
	handleCommentEditButton(event, pIDComment)
	{
		if (!pIDComment)
		{
			this.log.error(`No Comment ID; can't reply to comment that doesn't exist: .`);
			return;
		}
		// if user clicked the edit button
		if (event)
		{
			// if editable container exists
			if (document.getElementById(`CommentCurrent-Edit-Comment_${pIDComment}`))
			{
				// swap readonlies for inputs
				document.getElementById(`CommentCurrent-Readonly-Comment_${pIDComment}`).classList.add('is-hidden');
				document.getElementById(`CommentCurrent-Edit-Comment_${pIDComment}`).classList.remove('is-hidden');
			}
		}
	}

	/**
	 * @spec puts a loading spinner on the element for a short time: 1.5 seconds.
	 * @param {HTMLElement} htmlElement
	 */
	animateLoading(htmlElement)
	{
		htmlElement.classList.add('is-loading');
		setTimeout(() => htmlElement.classList.remove('is-loading'), 1500);
	}

	/**
	 * Handles the deletion of a comment.
	 * @param {Event} event - The event that triggered the delete action.
	 * @param {string} pIDComment - The ID of the comment to delete.
	 * @return {void}
	 */
	handleCommentDeleteButton(event, pIDComment)
	{
		// the body and footer templates for the delete modal
		const deleteModalBodyTemplate = /*html*/`
		<span id="DeleteModalBody">
			<div class="cell is-12">
				<div class="field">
					<p class="subtitle is-6 has-text-centered">Are you sure you want to delete this comment?</p>
					<p class="subtitle is-6 has-text-centered">Please click button below to delete or close this modal.</p>
				</div>
			</div>
		</span>`;
		const deleteModalFooterTemplate = /*html*/`
		<div id="DeleteModalFooter" class="buttons is-right">
			<button class="button is-danger is-soft" type="button" title="Cancel Save" onclick="_Pict.fable.services.HeadlightModal.dismissModals()">
				<span class="icon">
					<i class="fas fa-fw fa-times" aria-hidden="true"></i>
				</span>
				<span>Cancel</span>
			</button>
			<button class="button is-danger" type="button" title="Delete Comment" onclick="_Pict.views.CommentCurrent.handleCommentDelete(${pIDComment}, event)">
				<span class="icon">
					<i class="fas fa-fw fa-trash" aria-hidden="true"></i>
				</span>
				<span>Delete</span>
			</button>
		</div>`;
		// the options for the edit modal
		const deleteModalOptions = {
			title: 'Delete Comment?',
			modalBodyTemplate: deleteModalBodyTemplate,
			modalFooterTemplate: deleteModalFooterTemplate,
			confirmText: 'Delete',
			cancelText: 'Cancel',
		};
		this.fable.HeadlightModal.spawnModal(deleteModalOptions, this.confirmDeleteCallback.bind(this));
	}

	/**
	 * Handles deleting comment
	 * @param {number} pIDComment - The ID of the comment to delete.
	 * @param {Event} event - The event that triggered the save action.
	 * @return {Promise<void>}
	 * @async
	 */
	async handleCommentDelete(pIDComment, event)
	{
		if (event)
		{
			if (event.currentTarget instanceof HTMLElement)
			{
				event.currentTarget.classList.add('is-loading');
			}
		}

		await this.pict.providers.CommentProvider.deleteComment(pIDComment, (pResult) =>
		{
			if (pResult)
			{
				this.fetchComments(event, null, null);
			}
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
			this.pict.views.HeadlightPanels.toast({
				message: 'Comment Deleted',
				type: 'is-success',
			});
		});
		this.pict.fable.services.HeadlightModal.dismissModals();
	}

	/**
	 * Handles updating an existing comment
	 * @param {Event} event - The event that triggered the update
	 * @param {number} pIDComment - The ID of the comment being updated
	 * @param {string} pRowID - The ID of the row in the treeview
	 * @return {Promise<void>}
	 */
	async applyCommentEdit(event, pIDComment, pRowID)
	{
		// set the loading ui state
		if (event?.currentTarget instanceof HTMLElement)
		{
			event.currentTarget.classList.add('is-loading');
		}

		const elValue = document.getElementById(`CommentCurrent-Edit-Textarea-Comment_${pIDComment}`);
		const tmpCommentValue = elValue ? /** @type {HTMLTextAreaElement} */ (elValue).value.trim() : '';

		if (!tmpCommentValue)
		{
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
			this.pict.views.HeadlightPanels.toast({
				message: 'Comment cannot be empty.',
				type: 'is-warning',
			});
			return;
		}

		const tmpComment = {
			IDCustomer: this.AppData?.DocumentData?.ReportData?.IDCustomer,
			IDComment: pIDComment,
			Comment: tmpCommentValue,
		};

		await this.pict.providers.CommentProvider.updateComment(tmpComment, (pResult) =>
		{
			if (pResult)
			{
				this.fetchComments(event, pRowID, true);
			}
			this.pict.providers.UtilityProvider.removeAllLoadingClasses();
			this.pict.views.HeadlightPanels.toast({
				message: 'Comment Updated',
				type: 'is-success',
			});
		});
		this.pict.fable.services.HeadlightModal.dismissModals();
	}

	/**
	 * Handles adding a new comment or a reply
	 * @param {Event} event - The event that triggered the save
	 * @param {number} [pParentID] - ID of the parent comment (0 for new top-level comment)
	 * @return {Promise<void>}
 	*/
	async handleCommentAdd(event, pParentID)
	{
		const elReply = document.getElementById(`CommentCurrent-Reply-Textarea-Comment_${pParentID}`);
		const tmpCommentReplyValue = elReply ? /** @type {HTMLTextAreaElement} */ (elReply).value.trim() : '';

		const elNewValue = document.getElementById('CommentCurrent-Add-Comment-Textarea');
		const tmpCommentNewValue = elNewValue ? /** @type {HTMLTextAreaElement} */ (elNewValue).value.trim() : '';

		if (event?.currentTarget instanceof HTMLElement)
		{
			event.currentTarget.classList.add('is-loading');
		}

		// Add reply comment
		if (pParentID)
		{
			if (!tmpCommentReplyValue)
			{
				this.pict.providers.UtilityProvider.removeAllLoadingClasses();
				this.pict.views.HeadlightPanels.toast({
					message: 'Reply cannot be empty.',
					type: 'is-warning',
				});
				return;
			}
			// Create a new comment object for the reply
			const tmpComment = {
				IDCustomer: this.AppData?.DocumentData?.ReportData?.IDCustomer,
				IDComment: 0,
				IDParent: pParentID,
				Comment: tmpCommentReplyValue,
			};

			await this.pict.providers.CommentProvider.createComment(tmpComment, async (pResult) =>
			{
				this.pict.providers.UtilityProvider.removeAllLoadingClasses();
				this.pict.views.HeadlightPanels.toast({
					message: 'Reply Added',
					type: 'is-success',
				});
				if (pResult)
				{
					const newCommentID = pResult.IDComment;
					if (Array.isArray(this.AppData?.DocumentData?.ReportData?.Comments))
					{
						this.AppData.DocumentData.ReportData.Comments.push(newCommentID);
					}

					await this.pict.providers.CommentProvider.addCommentToDocument(this.AppData?.DocumentData?.ReportData?.IDDocument, newCommentID, (pNewResult) =>
					{
						if (pNewResult)
						{
							this.fetchComments(event, null, null);
						}
					});
				}
			});
		}
		// Add top-level comment
		else
		{
			if (!tmpCommentNewValue)
			{
				this.pict.providers.UtilityProvider.removeAllLoadingClasses();
				this.pict.views.HeadlightPanels.toast({
					message: 'Comment cannot be empty.',
					type: 'is-warning',
				});
				return;
			}
			// Create a new comment object for the new comment
			const tmpComment = {
				IDCustomer: this.AppData?.DocumentData?.ReportData?.IDCustomer,
				IDComment: 0,
				IDParent: 0,
				Comment: tmpCommentNewValue,
			};

			await this.pict.providers.CommentProvider.createComment(tmpComment, async (pResult) =>
			{
				this.pict.providers.UtilityProvider.removeAllLoadingClasses();
				this.pict.views.HeadlightPanels.toast({
					message: 'Comment Added',
					type: 'is-success',
				});
				if (pResult)
				{
					const newCommentID = pResult.IDComment;
					if (Array.isArray(this.AppData?.DocumentData?.ReportData?.Comments))
					{
						this.AppData.DocumentData.ReportData.Comments.push(newCommentID);
					}

					await this.pict.providers.CommentProvider.addCommentToDocument(this.AppData?.DocumentData?.ReportData?.IDDocument, newCommentID, (pNewResult) =>
					{
						if (pNewResult)
						{
							this.fetchComments(event, null, null);
							// if the comment was created successfully, clear out the comment container in the UI
							const elNewComment = document.getElementById(`CommentCurrent-Add-Comment-Textarea`);
							if (elNewComment)
							{
								/** @type {HTMLTextAreaElement} */ (elNewComment).value = '';
							}
						}
					});
				}
			});
		}
		// hide the add new comment container
		this.pict.fable.services.HeadlightModal.dismissModals();
	}

	/**
	 * Callback to run when user confirms inspection type save
	 * @param {object} event - The click event
	 */
	async confirmSaveCallback(event)
	{
		if (event)
		{
			// render the current state of the comment panel
			this.pict.views.CommentCurrent.renderAsync(() =>
			{
				this.pict.log.info('Comment Panel Rendered');
			});
		}
	}

	/**
	 * Callback to run when user confirms comment delete
	 * @param {object} event - The click event
	 */
	async confirmDeleteCallback(event)
	{
		if (event)
		{
			// render the current state of the comment panel
			this.pict.views.CommentCurrent.renderAsync(() =>
			{
				this.pict.log.info('Comment Panel Rendered');
			});
		}
	}

	/**
	 * Toggle the visibility of comments with responses
	 * @param {*} event - the event object
	 * @param {string} _RowID - the name and id of the comment
	 * @return {void}
	 */
	toggleSectionVisibility(event, _RowID)
	{
		// find the icon in the button and swap it out
		const tmpIcon = event?.currentTarget?.querySelector('i') || event?.parentElement?.currentTarget?.querySelector('i');
		if (!tmpIcon)
		{
			return;
		}
		if (tmpIcon.classList.contains('fa-chevron-left'))
		{
			tmpIcon.classList.remove('fa-chevron-left');
			tmpIcon.classList.add('fa-chevron-down');
		}
		else
		{
			tmpIcon.classList.remove('fa-chevron-down');
			tmpIcon.classList.add('fa-chevron-left');
		}
		// toggle the visibility of the child folders
		const tmpElementID = `CommentCurrent-Child-${_RowID}`;
		// if the element is not found, return
		if (!document.getElementById(tmpElementID))
		{
			this.log.error(`Element with ID ${tmpElementID} not found`);
			return;
		}
		document.getElementById(tmpElementID)?.classList.toggle('is-hidden', document.getElementById(tmpElementID)?.classList.contains('is-hidden') ? false : true);
	}
}

module.exports = CommentCurrent;

module.exports.default_configuration =
{
	ViewIdentifier: 'CommentCurrent',
	DefaultRenderable: 'CommentCurrent-Wrapper',
	DefaultDestinationAddress: '#HeadlightPanels-RightPanel',
	Templates:
		[
			{
				Hash: 'CommentCurrent-Wrapper',
				Template: /*html*/`
				<article id="HeadlightPanels-CommentCurrentPanel" class="panel is-active panel-content panel-has-nested-scroll-content">
					<p id="HeadlightPanels-CommentCurrentPanel-Header" class="panel-heading pr-4">
						<button type="button" class="is-pulled-right hl-panel-close-button" aria-label="close" title="Close panel" onclick="_Pict.views.HeadlightPanels.closeThisPanel(event, 'Comments')">
							<span class="icon">
								<i class="fas fa-fw fa-times-circle" aria-hidden="true"></i>
							</span>
						</button>
						<button type="button" class="is-pulled-right hl-panel-pin-button mr-2" aria-label="pin" title="Pin content" onclick="_Pict.views.HeadlightPanels.togglePanelPinState('right', 'Comments')">
							<span class="icon">
								<i class="fas fa-fw fa-thumbtack" aria-hidden="true"></i>
							</span>
						</button>
						<span class="icon">
							<i id="HeadlightPanels-CommentCurrent-Header-Icon" class="fas fa-fw fa-comment" aria-hidden="true"></i>
						</span>
						<span id="HeadlightPanels-CommentCurrent-Header-Title" class="mr-3">Comments</span>
					</p>
					<div class="panel-content nested-scroll-content">
						<div id="CommentCurrent-Entry-Container">
							{~T:CommentCurrent-Entry~}
						</div>
						<div id="HeadlightPanels-CommentCurrent-Content" class="has-bottom-navbar-offset">
							{~T:CommentCurrent-Results~}
						</div>
					</div>
				</article>`,
			},
			{
				Hash: 'CommentCurrent-Entry',
				Template: /*html*/`
				<div id="CommentCurrent-Entry-Container" class="container">
					<div class="field p-4">
						<div class="buttons is-right">
							<button class="button is-primary is-fullwidth" id="CommentCurrent-AddButton" title="Add comment" onclick="_Pict.views.CommentCurrent.addNewComment(event)">
								<i class="fas fa-fw fa-plus mr-1"></i> Create New Comment
							</button>
						</div>
					</div>
				</div>
				{~T:CommentCurrent-Add-Comment~}`,
			},
			{
				Hash: 'CommentCurrent-Add-Comment',
				Template: /*html*/`
                <div id="CommentCurrent-Add-Comment-Container" class="column is-hidden">
                    <div class="field">
                        <label class="label block" for="CommentCurrent-Add-Comment-Textarea">Comment</label>
                        <div id="CommentCurrent-Add-Comment-Edit" class="control is-fullwidth is-relative">
                            <textarea
                                id="CommentCurrent-Add-Comment-Textarea"
                                class="textarea"
                                placeholder="Enter a comment..."
                                rows="2"
                            ></textarea>
                        </div>
                    </div>
                    {~T:CommentCurrent-New-Comment-Buttons:Record~}
                </div>`,
			},
			{
				Hash: 'CommentCurrent-Results',
				Template: /*html*/`
				<div id="CommentCurrent-Results-Container" class="container table-container">
					{~TIfAbs:CommentCurrent-Results-Empty:Context[0]:Context[0].CurrentSearchResults.length^==^0~}
					{~TIfAbs:CommentCurrent-Container:Context[0]:Context[0].CurrentSearchResults.length^>^0~}
				</div>`,
			},
			{
				Hash: 'CommentCurrent-Results-Empty',
				Template: /*html*/`
				<div class="notification m-3 empty-message">
					<p class="has-text-centered">
						<span class="icon">
							<i class="fas fa-search"></i>
						</span>
						<span>No comments found.</span>
					</p>
				</div>`,
			},
			{
				Hash: 'CommentCurrent-Results-User',
				Template: /*html*/`{~D:Record.NameFirst~} {~D:Record.NameLast~}`,
			},
			/* ----- start Tree View ---- */
			{
				Hash: 'CommentCurrent-Container',
				Template: /*html*/`{~TS:CommentCurrent:Record.CurrentSearchResults~}`,
			},
			/* ----- Tree View > first layer (results or empty check) ----- */
			{
				Hash: 'CommentCurrent',
				Template: /*html*/`{~T:CommentCurrent-CommentList:Record~}`,
			},
			/* ----- Tree View > second layer (empty) ----- */
			{
				Hash: 'CommentCurrent-Empty',
				Template: /*html*/`
				<div class="notification m-3 p-2 empty-message has-background-text-30">
					<p class="has-text-centered">
						<span class="icon">
							<i class="fa fa-fw fa-info-circle"></i>
						</span>
						No responses found for this comment.
					</p>
				</div>`,
			},
			/* ----- Tree View > second layer (search results processed list) ----- */
			{
				Hash: 'CommentCurrent-Content',
				Template: /*html*/`{~T:CommentCurrent-CommentList:Record~}`,
			},
			{
				Hash: 'CommentCurrent-Child-Content',
				Template: /*html*/`{~T:CommentCurrent-Child-CommentList:Record~}`,
			},
			/* ----- Tree View > third layer (comments) ----- */
			{
				Hash: 'CommentCurrent-CommentList',
				Template: /*html*/`
				<div class="comment mx-3 mb-3" style="border-radius: 15px; overflow: hidden;">
					<div class="panel-block-group">
						<div
							id="CommentCurrent-{~D:Record._RowID~}"
							class="panel-block panel-block-group-header is-relative p-0 has-background-primary-25">
							<div class="navbar-item">
								<span class="icon">
									<i class="fas fa-solid fa-user-circle fa-xl"></i>
								</span>
								<span class="icon-text">{~D:Record.CreatingUserData.NameFirst~} {~D:Record.CreatingUserData.NameLast~}</span>
							</div>
						</div>
						<div class="p-3 has-background-text-30">
							<div id="CommentCurrent-Readonly-{~D:Record._RowID~}" class="is-size-6 has-text-text-100">{~D:Record.Comment~}</div>
							<p id="CommentCurrent-Edit-{~D:Record._RowID~}" class="control is-fullwidth is-relative is-hidden">
								<textarea
									id="CommentCurrent-Edit-Textarea-{~D:Record._RowID~}"
									class="textarea has-background-text-30"
									rows="2"
								>{~D:Record.Comment~}</textarea>
								{~T:CommentCurrent-Edit-Comment-Buttons:Record~}
							</p>
							<div class="help has-text-text-85 mt-2">{~DateFormat:Record.Date^MMMM DD, YYYY - hh:mm a~}
								{~T:CommentCurrent-ActionMenu:Record~}
							</div>
						</div>
					</div>
					<div id="CommentCurrent-Child-{~D:Record._RowID~}" class="is-hoverable panel-block-group">
						<div class="treeview-child">
							{~TS:CommentCurrent-Child-Content:Record.Children~}
							{~T:CommentCurrent-Reply-Comment~}
						</div>
					</div>
				</div>`,
			},
			/* ----- Tree View > third layer (comments) ----- */
			{
				Hash: 'CommentCurrent-Child-CommentList',
				Template: /*html*/`
				<div class="comment">
					<div class="panel-block-group" style="border-left: 10px solid var(--bulma-text-40);">
						<div
							id="CommentCurrent-{~D:Record._RowID~}"
							class="panel-block panel-block-group-header is-relative p-0 has-background-text-20">
							<div class="navbar-item">
								<span class="icon">
									<i class="fas fa-solid fa-user-circle fa-xl"></i>
								</span>
								<span class="icon-text">{~D:Record.CreatingUserData.NameFirst~} {~D:Record.CreatingUserData.NameLast~}</span>
							</div>
						</div>
						<div class="p-3 has-background-text-30">
							<div id="CommentCurrent-Readonly-{~D:Record._RowID~}" class="is-size-6 has-text-text-100">{~D:Record.Comment~}</div>
							<p id="CommentCurrent-Edit-{~D:Record._RowID~}" class="control is-fullwidth is-relative is-hidden">
								<textarea
									id="CommentCurrent-Edit-Textarea-{~D:Record._RowID~}"
									class="textarea has-background-text-30"
									rows="2"
								>{~D:Record.Comment~}</textarea>
								{~T:CommentCurrent-Edit-Comment-Buttons:Record~}
							</p>
							<div class="help has-text-text-85 mt-2">{~DateFormat:Record.Date^MMMM DD, YYYY - hh:mm a~}
								{~T:CommentCurrent-ActionMenu:Record~}
							</div>
						</div>
					</div>
				</div>`,
			},
			{
				Hash: 'CommentCurrent-ActionMenu',
				Template: /*html*/`
				<ul class="is-inline-flex list-menu visible-mode_{~D:Pict.views.CommentCurrent.SearchPanelMode~}">
					{~TIfAbs:CommentCurrent-Reply-Button:Record:Record.IDParent^==^0~}
					{~TIfAbs:CommentCurrent-Edit-Button:Record:Record.IsCreatingUser^==^1~}
					{~TIfAbs:CommentCurrent-Delete-Button:Record:Record.IsCreatingUser^==^1~}
				</ul>`,
			},
			{
				Hash: 'CommentCurrent-Reply-Button',
				Template: /*html*/
				`<li class="mx-2 list-menu-item mode_comment-reply">
					<a class="move-table-row-down-button has-text-primary-70" onclick="_Pict.views.CommentCurrent.handleCommentReplyButton(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-reply"></i>
						</span>
						<span>Reply</span>
					</a>
				</li>`,
			},
			{
				Hash: 'CommentCurrent-Delete-Button',
				Template: /*html*/
				`<li class="mx-2 list-menu-item mode_comment-delete">
					<a class="delete-table-row-button has-text-primary-70" onclick="_Pict.views.CommentCurrent.handleCommentDeleteButton(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-trash"></i>
						</span>
						<span>Delete</span>
					</a>
				</li>`,
			},
			{
				Hash: 'CommentCurrent-Edit-Button',
				Template: /*html*/
				`<li class="mx-2 list-menu-item mode_comment-edit">
					<a class="move-table-row-down-button has-text-primary-70" onclick="_Pict.views.CommentCurrent.handleCommentEditButton(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-pencil"></i>
						</span>
						<span>Edit</span>
					</a>
				</li>`,
			},
			{
				Hash: 'CommentCurrent-Reply-Comment',
				Template: /*html*/`
				<div id="CommentCurrent-Reply-Comment-Container_{~D:Record.IDComment~}" class="column has-background-text-30 is-hidden" style="border-left: 10px solid var(--bulma-text-40);">
					<textarea 
						id="CommentCurrent-Reply-Textarea-Comment_{~D:Record.IDComment~}"
						class="textarea has-background-text-25"
						rows="2"
						placeholder="Enter a reply to this comment..."
					></textarea>
					{~T:CommentCurrent-New-Comment-Buttons:Record~}
				</div>`,
			},
			{
				Hash: 'CommentCurrent-New-Comment-Buttons',
				Template: /*html*/
				`<span class="buttons is-right mt-2">
					<button class="button is-primary is-dark" type="button" title="Cancel Save" onclick="_Pict.views.CommentCurrent.cancelCommentEdit(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-fw fa-times" aria-hidden="true"></i>
						</span>
						<span>Cancel</span>
					</button>
					<button class="button is-primary" type="button" title="Save Comment" onclick="_Pict.views.CommentCurrent.handleCommentAdd(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-fw fa-comment" aria-hidden="true"></i>
						</span>
						<span>Save</span>
					</button>
				</span>`,
			},
			{
				Hash: 'CommentCurrent-Edit-Comment-Buttons',
				Template: /*html*/
				`<span class="buttons is-right mt-2">
					<button class="button is-primary is-dark" type="button" title="Cancel Save" onclick="_Pict.views.CommentCurrent.cancelCommentEdit(event, '{~D:Record.IDComment~}')">
						<span class="icon">
							<i class="fas fa-fw fa-times" aria-hidden="true"></i>
						</span>
						<span>Cancel</span>
					</button>
					<button class="button is-primary" type="button" title="Save Comment" onclick="_Pict.views.CommentCurrent.applyCommentEdit(event, '{~D:Record.IDComment~}', '{~D:Record._RowID~}')">
						<span class="icon">
							<i class="fas fa-fw fa-comment" aria-hidden="true"></i>
						</span>
						<span>Save</span>
					</button>
				</span>`,
			},
			/* ----- end Tree View ----- */
		],
	Renderables:
		[
			{
				RenderableHash: 'CommentCurrent-Wrapper',
				TemplateHash: 'CommentCurrent-Wrapper',
				DestinationAddress: '#HeadlightPanels-RightPanel',
			},
			{
				RenderableHash: 'CommentCurrent-Results',
				TemplateHash: 'CommentCurrent-Results',
				DestinationAddress: '#CommentCurrent-Results-Container',
			},
			{
				RenderableHash: 'CommentCurrent-Pagination',
				TemplateHash: 'CommentCurrent-Pagination',
				DestinationAddress: '#CommentCurrent-Pagination-Container',
			},
		],
};

